import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable } from 'rxjs';
import { CompanyResponse, ImportsService } from '@src/app/shared/backend/clients';
import { ImportType } from '@src/app/shared/enums/ImportType.enum';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { Subscription } from 'rxjs';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { SessionService } from '@src/app/shared/services/session.service';
import { Features } from '@src/app/shared/backend/auth/Feature';

@Component({
    selector: 'app-upload-data',
    templateUrl: './upload-data.component.html',
    styleUrl: './upload-data.component.scss',
    host: {
        class: 'app-upload-data'
    },
    standalone: false
})
export class UploadDataComponent implements OnInit {

  dataTypesList: any[] = [];
  templateDownloadLink: string = '';
  file: File | null = null;
  fileName: string = '';
  fileUploadError: string = '';
  selectedImportType: string = '';

  allowImportCardOrder: boolean = false;
  allowImportInventory: boolean = false;
  allowImportShipment: boolean = false;
  allowImportRetailer: boolean = false;
  allowImportItem: boolean = false;

  company: CompanyResponse = {
    inventorySubmissionThresholdDays: 0
  };

  public importDataForm: FormGroup = this.fb.group({
    dataType: [[], [Validators.required]],
    file: [null, [Validators.required]]
  });

  private csvBucketSubscription: Subscription = new Subscription();

  constructor(
    private router: Router,
    private toasterService: ToasterService,
    private translate: TranslateService,
    private importsService: ImportsService,
    private permissionService: PermissionService,
    private session: SessionService,
    private fb: FormBuilder
  ) { }

  async ngOnInit() {
    this.allowImportCardOrder = await this.permissionService.hasActionPermissions(Actions.import_cardorderings) && await this.permissionService.hasFeaturePermissions(Features.CardOrdering);
    this.allowImportInventory =  await this.permissionService.hasActionPermissions(Actions.import_inventories) && await this.permissionService.hasFeaturePermissions(Features.Inventories);
    this.allowImportShipment = await this.permissionService.hasActionPermissions(Actions.import_shipments) && await this.permissionService.hasFeaturePermissions(Features.Shipments);
    this.allowImportRetailer = await this.permissionService.hasActionPermissions(Actions.import_retailers) && await this.permissionService.hasFeaturePermissions(Features.Retailers);
    this.allowImportItem = await this.permissionService.hasActionPermissions(Actions.import_items) && await this.permissionService.hasFeaturePermissions(Features.Products);

    this.company = await this.session.getCompanyData();

    this.loadImportTypes();

    this.importDataForm.get('dataType')?.valueChanges.subscribe((value: any[]) => {
      this.onDataTypeChange(value);
    });
  }

  loadImportTypes() {
    let allowedDataTypes: any[] = [];

    if(this.allowImportInventory){
      allowedDataTypes.push(ImportType.IMPORT_TYPE_INVENTORY);
    }
    if(this.allowImportShipment){
      allowedDataTypes.push(ImportType.IMPORT_TYPE_SHIPMENT);
    }
    if(this.allowImportRetailer){
      allowedDataTypes.push(ImportType.IMPORT_TYPE_RETAILER);
    }
    if(this.allowImportCardOrder){
      allowedDataTypes.push(ImportType.IMPORT_TYPE_CARD_ORDERING);
    }
    if(this.allowImportItem && this.company.isPrepaired){
      allowedDataTypes.push(ImportType.IMPORT_TYPE_ITEM_PREPAIRED);
    }
    if(this.allowImportItem && !this.company.isPrepaired){
      allowedDataTypes.push(ImportType.IMPORT_TYPE_ITEM);
    }

    this.dataTypesList = Object.keys(ImportType).map((key) => {
      const value = ImportType[key as keyof typeof ImportType];
      const item = {
        name: this.translate.instant(`ENUMS.${key}`),
        value
      };
      if(allowedDataTypes.includes(value)){
        return item;
      }
      return;
    }).filter(x => x != undefined);
  }

  onDataTypeChange(selectedType: any[] | null) {
    this.selectedImportType = selectedType?.[0] ?? '';
    this.templateDownloadLink = '';

    if (this.csvBucketSubscription) {
      this.csvBucketSubscription.unsubscribe();
    }

    if (!this.selectedImportType) {
      return;
    }

    // TS: Record<Key, Value> creates a type ( Key = ImportType, Value = () => Observable<any> )
    const apiEndpointMap: Record<ImportType, () => Observable<any>> = {
      [ImportType.IMPORT_TYPE_CARD_ORDERING]: () => this.importsService.apiImportsTemplateCardOrderingsGet(),
      [ImportType.IMPORT_TYPE_INVENTORY]: () => this.importsService.apiImportsTemplateInventoriesGet(),
      [ImportType.IMPORT_TYPE_RETAILER]: () => this.importsService.apiImportsTemplateRetailersGet(),
      [ImportType.IMPORT_TYPE_SHIPMENT]: () => this.importsService.apiImportsTemplateShipmentsGet(),
      [ImportType.IMPORT_TYPE_ITEM]: () => this.importsService.apiImportsTemplateItemsGet(),
      [ImportType.IMPORT_TYPE_ITEM_PREPAIRED]: () => this.importsService.apiImportsTemplateItemsPrepairedGet()
    };

    const apiCall = apiEndpointMap[this.selectedImportType as ImportType];

    if (apiCall) {
      this.csvBucketSubscription = apiCall().subscribe({
        next: (response: any) => {
          this.templateDownloadLink = response.url;
        },
        error: (err) => {
          this.toasterService.show('error', 'Error', err, 3000);
          console.error(`Error fetching ${this.selectedImportType} template download link:`, err);
          this.templateDownloadLink = '';
        }
      });
    }
  }

  downloadTemplateCSV() {
    if (this.templateDownloadLink) {
      const link = document.createElement('a');
      link.href = this.templateDownloadLink;

      link.download = `${this.selectedImportType}.csv`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  onFileRemove(event: any): void {
    this.fileName = '';
    this.importDataForm.get('file')?.reset();
  }

  onFileUpload(event: any): void {
    const uploadedFile = event.files[0];

    if (uploadedFile && uploadedFile.type !== 'text/csv') {
      this.fileUploadError = this.translate.instant('IMPORT_CREATE.FILE_VALIDATION');
      this.fileName = '';
      this.importDataForm.get('file')?.reset();
      return;
    }

    if (uploadedFile.size > 1024 * 1024) {
      this.fileUploadError = this.translate.instant('IMPORT_CREATE.FILE_SIZE_VALIDATION');
      this.fileName = '';
      this.importDataForm.get('file')?.reset();
      return;
    }

    this.file = uploadedFile;
    this.fileName = uploadedFile.name;
    this.importDataForm.get('file')?.setValue(uploadedFile);
    this.fileUploadError = '';
  }

  async uploadFile(): Promise<void> {
    if (this.importDataForm.valid && this.file) {

      let uploadEndpoint;
      switch (this.selectedImportType) {
        case ImportType.IMPORT_TYPE_CARD_ORDERING:
          uploadEndpoint = this.importsService.apiImportsCreateCardorderingsPost(this.file);
          break;

        case ImportType.IMPORT_TYPE_INVENTORY:
          uploadEndpoint = this.importsService.apiImportsCreateInventoriesPost(this.file);
          break;

        case ImportType.IMPORT_TYPE_RETAILER:
          uploadEndpoint = this.importsService.apiImportsCreateRetailersPost(this.file);
            break;

            case ImportType.IMPORT_TYPE_SHIPMENT:
          uploadEndpoint = this.importsService.apiImportsCreateShipmentsPost(this.file);
          break;

        case ImportType.IMPORT_TYPE_ITEM:
          uploadEndpoint = this.importsService.apiImportsCreateItemsPost(this.file);
          break;

        case ImportType.IMPORT_TYPE_ITEM_PREPAIRED:
          uploadEndpoint = this.importsService.apiImportsCreateItemsPrepairedPost(this.file);
            break;

        default:
          return;
      }

      try {
        await firstValueFrom(uploadEndpoint);
        this.router.navigate(['/imports']);
      } catch (error) {
        console.error('Upload failed:', error);
        this.toasterService.show('error', this.translate.instant('IMPORT_CREATE.TOAST_ERROR_TITLE_UPLOAD_FAILED'), this.translate.instant('IMPORT_CREATE.TOAST_ERROR_MESSAGE_UPLOAD_FAILED'));
      }
    }
  }

  isSubmitEnabled(): boolean {
    if (this.importDataForm.valid && this.file !== null && this.selectedImportType) {
      return true;
    } else {
      return false;
    }
  }

  ngOnDestroy() {
    if (this.csvBucketSubscription) {
      this.csvBucketSubscription.unsubscribe();
    }
  }
}
